import React from "react";
import {
  Formik,
  FormikActions,
  Form as FormikForm,
  Field,
  FormikProps,
} from "formik";
import { injectIntl } from "react-intl";
import TextInput from "../../text-input";
import InputErrorMessage from "../../input-error-message";

import messages from "../PageNewExerciswe.intl";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../../types/styled-arguments";
import { required } from "../../form-utils";
import ObjectivesMultiSelect from "./ObjectivesMultiSelect";
import CategoryComponent from "./CategoryComponent";
import { TextField, Box } from "@material-ui/core";
import TitleIcon from "@material-ui/icons/Title";
import DescriptionIcon from "@material-ui/icons/Description";
import CategoryIcon from "@material-ui/icons/Category";
import FlagIcon from "@material-ui/icons/Flag";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

export interface IFormValues {
  name: string;
  category: string;
  subcategory: string;
  objectives: string[];
  shortDescription: String;
  taskDevelopment: String;
}

const StyledForm = styled(FormikForm)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  flex: 1,
}));

const FormSection = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "16px",
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  transition: "border-color 0.2s ease",
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
  },
}));

const SectionHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2.5),
}));

const SectionIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 42,
  height: 42,
  borderRadius: "12px",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "1.3rem",
    color: theme.palette.primary.main,
  },
}));

const SectionTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  fontSize: "1rem",
  color: theme.palette.primary.dark,
  letterSpacing: "0.3px",
}));

const StyledTextField = styled(TextField)(({ theme }: IStyledArguments) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: theme.palette.background.paper,
    fontSize: "1rem",
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.extra.borderColor,
  },
  "& .MuiInputLabel-outlined": {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: "0.95rem",
    "&.Mui-focused": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px 16px",
  },
}));

const SubmitButton = styled("button")(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "#fff",
  fontWeight: 700,
  borderRadius: "16px",
  padding: theme.spacing(2.25, 5),
  fontSize: "1.1rem",
  border: "none",
  cursor: "pointer",
  transition: "background 0.2s ease",
  marginTop: theme.spacing(4),
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  letterSpacing: "0.5px",
  "&:hover:not(:disabled)": {
    background: theme.palette.primary.dark,
  },
  "&:disabled": {
    background: "#bdbdbd",
    color: "rgba(255, 255, 255, 0.7)",
    cursor: "not-allowed",
  },
  "& svg": {
    fontSize: "1.3rem",
    transition: "transform 0.2s ease",
  },
  "&:hover:not(:disabled) svg": {
    transform: "translateX(5px)",
  },
}));

const RequiredBadge = styled("span")(() => ({
  background: "rgba(244, 67, 54, 0.1)",
  color: "#f44336",
  fontSize: "0.7rem",
  fontWeight: 600,
  padding: "3px 10px",
  borderRadius: "8px",
  marginLeft: "auto",
}));

interface IForm {
  onSubmit: (arg: IFormValues) => void;
  checkValid: boolean;
  name: string;
  shortDescription: string;
  taskDevelopment: string;
  category: string;
  subcategory: string;
  objectives: string[];
}

const NewExerciseForm = injectIntl<IForm>(
  ({
    onSubmit,
    checkValid,
    name,
    category,
    subcategory,
    objectives,
    taskDevelopment,
    shortDescription,
    intl,
  }) => {
    return (
      <Formik
        initialValues={{
          name,
          category,
          subcategory,
          objectives,
          taskDevelopment,
          shortDescription,
        }}
        isInitialValid={checkValid}
        onSubmit={(
          values: IFormValues,
          actions: FormikActions<IFormValues>
        ) => {
          onSubmit(values);
          actions.setSubmitting(false);
        }}
        render={({
          errors,
          touched,
          isValid,
          setFieldValue,
        }: FormikProps<IFormValues>) => (
          <StyledForm data-testid="add-player-form">
            <FormSection>
              <SectionHeader>
                <SectionIcon>
                  <TitleIcon />
                </SectionIcon>
                <SectionTitle>Drill Name</SectionTitle>
                <RequiredBadge>Required</RequiredBadge>
              </SectionHeader>
              <Field
                name="name"
                validate={required(intl)}
                error={errors.name && touched.name}
                component={TextInput}
                inputProps={{
                  "data-testid": "name",
                }}
                label={intl.formatMessage(messages.name)}
              />
              <InputErrorMessage name="name" />
            </FormSection>
            
            <FormSection>
              <SectionHeader>
                <SectionIcon>
                  <DescriptionIcon />
                </SectionIcon>
                <SectionTitle>Short Description</SectionTitle>
                <RequiredBadge>Required</RequiredBadge>
              </SectionHeader>
              <Field
                name="shortDescription"
                validate={required(intl)}
                error={errors.shortDescription && touched.shortDescription}
                component={TextInput}
                inputProps={{
                  "data-testid": "shortDescription",
                }}
                label={intl.formatMessage(messages.shortDescription)}
              />
              <InputErrorMessage name="shortDescription" />
            </FormSection>

            <FormSection>
              <SectionHeader>
                <SectionIcon>
                  <CategoryIcon />
                </SectionIcon>
                <SectionTitle>Category & Subcategory</SectionTitle>
                <RequiredBadge>Required</RequiredBadge>
              </SectionHeader>
              <CategoryComponent
                label={intl.formatMessage(messages.category)}
                title={"new Category"}
                callback={({ category, subcategory }: any) => {
                  setFieldValue("category", category);
                  setFieldValue("subcategory", subcategory);
                }}
              />
            </FormSection>

            <FormSection>
              <SectionHeader>
                <SectionIcon>
                  <FlagIcon />
                </SectionIcon>
                <SectionTitle>Training Objectives</SectionTitle>
              </SectionHeader>
              <ObjectivesMultiSelect
                callback={(objectives: string[]) => {
                  setFieldValue("objectives", objectives);
                }}
              />
            </FormSection>

            <FormSection>
              <SectionHeader>
                <SectionIcon>
                  <AssignmentIcon />
                </SectionIcon>
                <SectionTitle>Task Development</SectionTitle>
              </SectionHeader>
              <StyledTextField
                variant="outlined"
                placeholder="Describe how the drill should be executed, including player positions, movements, and key coaching points..."
                label="Task Development"
                multiline
                rows={6}
                fullWidth
                onChange={(e) =>
                  setFieldValue("taskDevelopment", e.target.value)
                }
              />
            </FormSection>

            <SubmitButton
              type="submit"
              data-testid="go-next-button"
              disabled={!isValid}
            >
              Create Drill
              <ArrowForwardIcon />
            </SubmitButton>
          </StyledForm>
        )}
      />
    );
  }
);

export default NewExerciseForm;
